import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, ArrowSquareOut, Cloud, MagnifyingGlass, WarningCircle, X } from '@phosphor-icons/react';
import { ImmoButton, LeerZustand, ObjektPicker, SegmentedControl, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { supabase } from '../../../../lib/supabase/client';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { formatiereGroesse } from '../../../../lib/dokumente/aktenschrankApi';
import { suchWoerter } from '../../../../lib/dokumente/aktenschrank';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { dateiDarstellung, deDatumZeit } from '../aktenschrank/dateiDarstellung';
import { UebernahmeKarte } from './UebernahmeKarte';
import { PruefBereich } from './PruefBereich';

interface AltDatei {
  id: string; datei_name: string | null; titel: string | null; kategorie: string | null; onedrive_url: string;
  onedrive_modified_at: string | null; datei_groesse: number | null; gdrive_file_id: string | null; objekt: { objektnummer: string | null; bezeichnung: string | null } | null;
}

const SEITE = 50;

/**
 * Altarchiv (Handoff Dokumente §6.7): OneDrive-Bestand bis 2025 nur lesend – Liste aus dokumente.onedrive_url
 * mit Suche, Objekt-Filter und „In OneDrive öffnen“. Seit #517 (17.10.2026) oben die Übernahme ins Google Drive.
 */
export function AltarchivTab({ userId, onOeffneInAkten }: { userId: string | null; onOeffneInAkten: (objektId: string | null, dateiId: string) => void }) {
  const { mobil } = useMobilModus();
  const [bereich, setBereich] = useState<'pruefen' | 'liste'>('pruefen');
  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [eingabe, setEingabe] = useState('');
  const [suche, setSuche] = useState('');
  const [dateien, setDateien] = useState<AltDatei[] | null>(null);
  const [anzahl, setAnzahl] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [runde, setRunde] = useState(0);
  const token = useRef(0);

  useEffect(() => { ladeObjekte().then(setObjekte).catch(() => setObjekte([])); }, []);
  useEffect(() => { const t = setTimeout(() => setSuche(eingabe), 350); return () => clearTimeout(t); }, [eingabe]);
  const optionen: ObjektPickerOption[] = useMemo(() => objekte.map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}`,
  })), [objekte]);

  const abfrage = (von: number) => {
    let q = supabase.from('dokumente')
      .select('id, datei_name, titel, kategorie, onedrive_url, onedrive_modified_at, datei_groesse, gdrive_file_id, objekt:objekt_id(objektnummer, bezeichnung)', { count: von === 0 ? 'exact' : undefined })
      .not('onedrive_url', 'is', null).eq('is_deleted', false);
    if (objektId) q = q.eq('objekt_id', objektId);
    for (const w of suchWoerter(suche)) q = q.or(`datei_name.ilike.%${w}%,titel.ilike.%${w}%,kategorie.ilike.%${w}%`);
    return q.order('onedrive_modified_at', { ascending: false, nullsFirst: false }).order('id').range(von, von + SEITE - 1);
  };

  useEffect(() => {
    const t = ++token.current;
    setDateien(null); setFehler(null);
    abfrage(0).then(({ data, error, count }) => {
      if (t !== token.current) return;
      if (error) { setFehler(error.message); return; }
      setDateien((data ?? []) as unknown as AltDatei[]);
      setAnzahl(count ?? null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abfrage hängt nur an objektId/suche
  }, [objektId, suche, runde]);

  const mehr = async () => {
    if (!dateien) return;
    const t = token.current;
    setLaedtMehr(true);
    const { data, error } = await abfrage(dateien.length);
    setLaedtMehr(false);
    if (t !== token.current) return;
    if (error) { setFehler(error.message); return; }
    setDateien(alt => [...(alt ?? []), ...((data ?? []) as unknown as AltDatei[])]);
  };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-center" style={{ borderRadius: 22, padding: '14px 16px', gap: 12, background: 'var(--info-soft)' }}>
        <Cloud weight="fill" size={24} color="var(--info)" style={{ flexShrink: 0 }} />
        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--info)' }}>OneDrive-Altarchiv · nur lesen</span>
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>Bestand bis 2025. Neue Dokumente landen im Aktenschrank (Google Drive).</span>
        </div>
      </div>

      <UebernahmeKarte />

      <SegmentedControl<'pruefen' | 'liste'> ariaLabel="Bereich des Altarchivs" wert={bereich} onChange={setBereich} hoehe={mobil ? 44 : 34}
        segmente={[{ key: 'pruefen', label: 'Vorschläge prüfen' }, { key: 'liste', label: 'Alle OneDrive-Dateien' }]} />

      {bereich === 'pruefen' && <PruefBereich userId={userId} onOeffneInAkten={onOeffneInAkten} />}

      {bereich === 'liste' && <>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <div className="flex items-center" style={{ gap: 6 }}>
          <ObjektPicker optionen={optionen} wert={objektId} onWahl={setObjektId} leerLabel="Alle Objekte" />
          {objektId && (
            <button type="button" aria-label="Objekt-Filter entfernen" onClick={() => setObjektId(null)} className="flex items-center justify-center cursor-pointer"
              style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)' }}><X weight="bold" size={13} /></button>
          )}
        </div>
        <label className="immo-glas flex items-center" style={{ flex: '1 1 240px', minWidth: 0, height: 44, borderRadius: 22, gap: 8, padding: '0 14px' }}>
          <MagnifyingGlass size={16} color="var(--ink3)" aria-hidden />
          <input value={eingabe} onChange={e => setEingabe(e.target.value)} placeholder="Im Altarchiv suchen…" aria-label="Im Altarchiv suchen"
            className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
        </label>
      </div>

      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={() => setRunde(r => r + 1)}>Erneut versuchen</ImmoButton>
        </div>
      ) : !dateien ? (
        <div className="animate-pulse" style={{ height: 320, borderRadius: 26, background: 'var(--fill2)' }} aria-busy="true" />
      ) : dateien.length === 0 ? (
        <LeerZustand icon={Cloud} titel="Keine Dateien gefunden" text={suche || objektId ? 'Für diese Suche gibt es im Altarchiv nichts.' : 'Im Altarchiv liegen keine Dateien.'} />
      ) : (
        <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 12, gap: 2 }} aria-label="Dateien im Altarchiv">
          <span className="immo-zahl" style={{ padding: '4px 8px 8px', fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
            {anzahl !== null ? `${anzahl.toLocaleString('de-DE')} ${anzahl === 1 ? 'Datei' : 'Dateien'}` : `${dateien.length} Dateien`}
          </span>
          {dateien.map(d => {
            const name = d.datei_name || d.titel || 'Datei';
            const s = dateiDarstellung(name);
            const sub = [d.objekt ? [d.objekt.objektnummer, d.objekt.bezeichnung].filter(Boolean).join(' ') : null, d.kategorie, formatiereGroesse(d.datei_groesse != null ? String(d.datei_groesse) : null), deDatumZeit(d.onedrive_modified_at)].filter(Boolean).join(' · ');
            return (
              <a key={d.id} href={d.onedrive_url} target="_blank" rel="noreferrer" className="immo-hover-flaeche flex items-center min-w-0"
                style={{ gap: 12, minHeight: 56, padding: '8px 10px', borderRadius: 16, color: 'var(--ink)', textDecoration: 'none' }} title="In OneDrive öffnen">
                <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: s.bg }}><s.icon weight="fill" size={19} color={s.fg} /></span>
                <span className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{name}</span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
                </span>
                {d.gdrive_file_id && (
                  <span className="shrink-0" style={{ height: 24, padding: '0 9px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 800, color: 'var(--ok)', background: 'var(--ok-soft)' }}>übernommen</span>
                )}
                <span className="flex items-center shrink-0" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--info)' }}>
                  <ArrowSquareOut weight="bold" size={15} />{!mobil && 'In OneDrive öffnen'}
                </span>
              </a>
            );
          })}
          {anzahl !== null && dateien.length < anzahl && (
            <ImmoButton groesse={44} icon={ArrowClockwise} disabled={laedtMehr} onClick={() => void mehr()} style={{ alignSelf: 'center', marginTop: 6 }}>
              {laedtMehr ? 'Wird geladen …' : `Weitere laden (${(anzahl - dateien.length).toLocaleString('de-DE')} übrig)`}
            </ImmoButton>
          )}
        </section>
      )}
      </>}
    </div>
  );
}
